import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Star, CheckCircle, CreditCard, Check, Zap } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import PlatformCapabilityUniverse from './PlatformCapabilityUniverse';
import PlanComparison from './PlanComparison';

/**
 * TierPicker — renders the 3 paid tiers + a "Start 14-day Free Trial" CTA.
 * Props:
 *   tiers: array of decorated tier objects (from useTiers)
 *   selected: currently-selected tier id
 *   onSelect: (tierId) => void
 *   onContinueToPayment: () => void
 *   onStartTrial: () => void
 *   isSubmitting: bool — disables CTAs while a request is in flight
 *   trialPending: bool — shows spinner state on the trial button
 */
export const TierPicker = ({
  tiers,
  selected,
  onSelect,
  onContinueToPayment,
  onStartTrial,
  isSubmitting,
  trialPending,
}) => {
  const { t } = useLanguage();
  const selectedTier = tiers.find(tier => tier.id === selected);
  return (
    <div className="space-y-6" data-testid="tier-picker">
      <div className="text-center">
        <h3 className="text-2xl font-semibold mb-2">{t('tier.choosePlan')}</h3>
        <p className="text-gray-600">{t('tier.chooseDesc')}</p>
      </div>

      {/* Trial CTA banner */}
      <div
        className="border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 flex flex-col md:flex-row items-center md:items-stretch justify-between gap-4"
        data-testid="trial-banner"
      >
        <div className="flex items-start space-x-3">
          <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Zap className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <div className="font-semibold text-emerald-900">{t('tier.trialTitle')}</div>
            <div className="text-sm text-emerald-800">
              {t('tier.trialDesc')}
            </div>
          </div>
        </div>
        <Button
          onClick={onStartTrial}
          disabled={isSubmitting || trialPending}
          data-testid="start-trial-btn"
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 whitespace-nowrap"
        >
          {trialPending ? t('tier.startingTrial') : t('tier.startTrial')}
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {tiers.map(tier => {
          const Icon = tier.icon;
          const isSelected = selected === tier.id;
          return (
            <Card
              key={tier.id}
              data-testid={`tier-card-${tier.id}`}
              className={`cursor-pointer transition-all duration-200 relative ${
                isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:shadow-lg hover:scale-105'
              }`}
              onClick={() => onSelect(tier.id)}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    <Star className="h-3 w-3 mr-1" />
                    {t('tier.popular')}
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center">
                  <Icon className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl">{tier.name}</CardTitle>
                <div className="text-3xl font-bold text-gray-900">
                  {(tier.on_request || tier.price == null) ? t('tier.onRequest') : <>${tier.price}<span className="text-sm text-gray-500">/{tier.interval}</span></>}
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4">
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>{t('tier.users')} {tier.limits.users}</div>
                    <div>{t('tier.engagements')} {tier.limits.engagements}</div>
                    <div>{t('tier.storage')} {tier.limits.storage}</div>
                  </div>
                </div>
                {isSelected && (
                  <div className="mt-4 p-2 bg-purple-100 rounded text-center">
                    <Check className="h-5 w-5 text-purple-600 mx-auto" />
                    <span className="text-sm font-medium text-purple-800">{t('tier.selected')}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* GOV13-A6 — platform-subscription explanation + authoritative catalogue-driven per-plan comparison. */}
      <PlatformCapabilityUniverse />
      <PlanComparison />

      <div className="text-center">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {t('tier.selectedLabel')} <span className="font-semibold">{selectedTier?.name}</span>
            {selectedTier && <span> — {(selectedTier.on_request || selectedTier.price == null) ? t('tier.onRequest') : `$${selectedTier.price}/${selectedTier.interval}`}</span>}
          </p>
        </div>
        {(selectedTier && (selectedTier.on_request || selectedTier.price == null)) ? (
          <>
            <Button
              onClick={() => { window.location.href = 'mailto:sales@servisfy.com?subject=Custom%20plan%20enquiry'; }}
              data-testid="continue-to-payment-btn"
              className="w-full max-w-md h-12 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
            >
              {t('tier.contactSales')}
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              {t('tier.customPlanNote')}
            </p>
          </>
        ) : (
          <>
            <Button
              onClick={onContinueToPayment}
              disabled={isSubmitting}
              data-testid="continue-to-payment-btn"
              className="w-full max-w-md h-12 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {isSubmitting ? t('tier.redirecting') : t('tier.continuePayment')}
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              {t('tier.securePayment')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default TierPicker;